import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { toast } from '@repo/toast';
import { ArrowLeftIcon, ArrowRightIcon, XIcon } from '@repo/icons';
import { Button, PasswordInput } from '@repo/ui';

import { AuthModalError, AuthModalException } from '@features/auth/hooks/useAuthModal';

interface Props {
  testId: TestId;
  email: string;
  confirmPassword: (password: string) => Promise<unknown>;
  requestPasswordReset: () => Promise<unknown>;
  changeEmail: () => void;
  validating: boolean;
  finalizing: boolean;
  requestingPasswordReset: boolean;
  initialEmail?: string;
  onClose?: () => void;
}

export default function PasswordSection({
  testId,
  email,
  confirmPassword,
  requestPasswordReset,
  changeEmail,
  validating,
  finalizing,
  requestingPasswordReset,
  initialEmail,
  onClose,
}: Props) {
  const [password, setPassword] = useState<string>('');

  const pending = validating || finalizing;

  const handleSubmit = async () => {
    if (pending || !password) return;
    try {
      await confirmPassword(password);
    } catch (error) {
      if (error instanceof AuthModalException && error.type === AuthModalError.InvalidPassword) {
        toast.danger(tr('auth:login_drawer.password.invalid_password'), testId.append('invalidPasswordToast'));
        return;
      }
      toast.danger(tr('auth:login_drawer.password.login_error'), testId.append('loginErrorToast'));
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleSubmit();
  };

  const handleForgotPassword = async () => {
    if (requestingPasswordReset) return;
    try {
      await requestPasswordReset();
      toast.success(tr('auth:login_drawer.password.reset_link_sent', { email }), testId.append('resetLinkSentToast'));
    } catch {
      toast.danger(tr('auth:login_drawer.password.reset_link_error'), testId.append('resetLinkErrorToast'));
    }
  };

  return (
    <>
      <div className="flex justify-between">
        <h2 className="text-heading text-xl font-semibold">{tr('auth:login_drawer.password.title')}</h2>
        {onClose && (
          <button aria-label={tr('actions.close')} className="cursor-pointer" type="button" onClick={onClose}>
            <XIcon size={20} />
          </button>
        )}
      </div>
      <p className="text-body text-sm">
        {tr('auth:login_drawer.password.description')} <span className="font-semibold">{email}</span>
      </p>

      <PasswordInput
        testId={testId.append('password')}
        placeholder={tr('auth:login_drawer.password.password_placeholder')}
        value={password}
        onChange={setPassword}
        onKeyDown={handleKeyDown}
        disabled={pending}
        autoFocus
      />

      <Button
        color="ghost"
        size="sm"
        className="self-start"
        testId={testId.append('forgotPasswordButton')}
        onClick={handleForgotPassword}
        pending={requestingPasswordReset}
      >
        {tr('auth:login_drawer.password.forgot_password')}
      </Button>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button
          color="secondary"
          onClick={initialEmail ? onClose : changeEmail}
          leftIcon={ArrowLeftIcon}
          testId={testId.append('changeEmailButton')}
        >
          {tr('auth:login_drawer.password.change_email')}
        </Button>
        <Button
          color="brand"
          testId={testId.append('submitPassword')}
          onClick={handleSubmit}
          rightIcon={ArrowRightIcon}
          disabled={!password.length}
          pending={pending}
        >
          {tr('auth:login_drawer.password.cta')}
        </Button>
      </div>
    </>
  );
}
